import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
  deleteUser,
  User as FirebaseUser,
} from "firebase/auth";
import {
  serverTimestamp,
  updateDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteField,
  onSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, auth, db } from "../firebase";
import type { CareerTestResult } from "../types/careerTest";
import type { CourseFinderAnswers } from "../types/CourseFinder";
import type { Student } from "../types/student";
import { computeProfileCompletion } from "../utils/profileCompletion";
import {
  finalizePendingLandingPageSubmissions,
} from "../lib/landingPageFinalizers";

const SESSION_LEAD_ID_KEY = "scs-education-session-lead-id";
const PENDING_CAREER_RESULT_KEY = "scs-education-pending-career-test-result";

type LeadMatchField = "ic" | "phoneNumber" | "email";

function normalizeEmail(email?: string): string {
  return (email || "").trim().toLowerCase();
}

function normalizePhone(phone?: string, countryCode?: string): string {
  const digits = (phone || "").replace(/\D/g, "");
  const code = (countryCode || "").replace(/\D/g, "");
  if (!digits) return "";
  if (code === "60" && digits.startsWith("0")) return digits.slice(1);
  return digits;
}

function normalizeIc(ic?: string): string {
  return (ic || "").replace(/\D/g, "");
}

function getPendingCareerTestResult(): CareerTestResult | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(PENDING_CAREER_RESULT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CareerTestResult;
  } catch {
    return null;
  }
}

function clearPendingCareerTestResult() {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(PENDING_CAREER_RESULT_KEY);
  }
}

function hydrateStudent(
  data: Partial<Student>,
  uid: string,
  email: string,
): Student {
  const student = {
    ...DEFAULT_STUDENT_DATA,
    ...data,
    id: uid,
    email: email || data.email || "",
  } as Student;

  return {
    ...student,
    profileCompletion: computeProfileCompletion(student),
  };
}

function getSessionLeadId(): string {
  if (typeof window === "undefined") return "";
  const existing = window.sessionStorage.getItem(SESSION_LEAD_ID_KEY);
  if (existing) return existing;
  const next = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  window.sessionStorage.setItem(SESSION_LEAD_ID_KEY, next);
  return next;
}

function appendSourceDetail(
  existing: string | undefined,
  ...nextSources: string[]
): string {
  const sources = (existing || "")
    .split(",")
    .map((source) => source.trim())
    .filter(Boolean);

  nextSources.forEach((source) => {
    if (source && !sources.includes(source)) {
      sources.push(source);
    }
  });

  return sources.join(", ");
}

const DEFAULT_STUDENT_DATA: Omit<Student, "id" | "email"> = {
  name: "",
  countryCode: "60",
  country: "Malaysia",
  phoneNumber: "",
  ic: "",
  dob: "",
  gender: "",
  nationality: "",
  state: "",
  school: {
    schoolName: "",
    schoolState: "",
    schoolYear: "",
  },
  spmYear: new Date().getFullYear(),
  spmGrades: [],
  pathway: "",
  interests: [],
  budget: "",
  avatarColor: "#7C3AED",
  points: 0,
  tier: "Bronze",
  joinedAt: new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }),
  counselorId: "",
  profileCompletion: 0,
};

interface AuthContextType {
  user: Student | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  profileLoadError: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<boolean>;
  register: (
    data: Partial<Student> & {
      password: string;
      courseFinderAnswers?: CourseFinderAnswers;
    },
  ) => Promise<boolean>;
  logout: () => void;
  updateUser: (updates: Partial<Student>) => Promise<void>;
  submitCourseFinderAnswers: (
    answers: Omit<
      CourseFinderAnswers,
      | "name"
      | "phoneNumber"
      | "email"
      | "password"
      | "fillerType"
      | "countryCode"
      | "country"
    > &
      Pick<Partial<Student>, "profileCompletion">,
  ) => Promise<boolean>;
  saveCareerTestResult: (result: CareerTestResult) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeLeads: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribeLeads) {
        unsubscribeLeads();
        unsubscribeLeads = undefined;
      }

      if (firebaseUser) {
        const uid = firebaseUser.uid;
        const email = firebaseUser.email || "";
        setProfileLoadError(null);
        const leadsQuery = query(
          collection(db, "leads"),
          where("studentUID", "==", uid),
        );
        unsubscribeLeads = onSnapshot(
          leadsQuery,
          (snapshot) => {
            if (!snapshot.empty) {
              const data = snapshot.docs[0].data();
              setUser(hydrateStudent(data as Partial<Student>, uid, email));
            } else {
              setUser({ ...DEFAULT_STUDENT_DATA, id: uid, email });
            }
            setProfileLoadError(null);
            setIsLoading(false);
          },
          (error) => {
            console.error("Subscribe student error:", error);
            setProfileLoadError(
              "We could not load your student profile. Please refresh and try again.",
            );
            setIsLoading(false);
          },
        );
      } else {
        setUser(null);
        setProfileLoadError(null);
        setIsLoading(false);
      }
    });
    return () => {
      if (unsubscribeLeads) unsubscribeLeads();
      unsubscribeAuth();
    };
  }, []);

  const upsertLeadRecord = async (leadData: Record<string, any>) => {
    try {
      const normalizedEmail = normalizeEmail(leadData.email);
      const normalizedPhone = normalizePhone(
        leadData.phoneNumber,
        leadData.countryCode,
      );
      const normalizedIc = normalizeIc(leadData.ic);
      const sessionLeadId = getSessionLeadId();
      const payload = {
        ...leadData,
        ...(normalizedEmail ? { email: normalizedEmail } : {}),
        ...(normalizedPhone ? { phoneNumber: normalizedPhone } : {}),
        ...(normalizedIc ? { ic: normalizedIc } : {}),
        ...(leadData.sessionLeadId ? {} : { sessionLeadId }),
        updatedAt: serverTimestamp(),
      };

      const candidateQueries: Array<{
        q: ReturnType<typeof query>;
        field: LeadMatchField | "studentUID" | "sessionLeadId";
      }> = [];
      if (leadData.studentUID) {
        candidateQueries.push({
          q: query(
            collection(db, "leads"),
            where("studentUID", "==", leadData.studentUID),
          ),
          field: "studentUID",
        });
      }
      if (sessionLeadId) {
        candidateQueries.push({
          q: query(
            collection(db, "leads"),
            where("sessionLeadId", "==", sessionLeadId),
          ),
          field: "sessionLeadId",
        });
      }
      if (normalizedEmail) {
        candidateQueries.push({
          q: query(
            collection(db, "leads"),
            where("email", "==", normalizedEmail),
          ),
          field: "email",
        });
      }
      if (normalizedPhone) {
        candidateQueries.push({
          q: query(
            collection(db, "leads"),
            where("phoneNumber", "==", normalizedPhone),
          ),
          field: "phoneNumber",
        });
      }
      if (normalizedIc) {
        candidateQueries.push({
          q: query(collection(db, "leads"), where("ic", "==", normalizedIc)),
          field: "ic",
        });
      }

      const candidates = new Map<string, { ref: any; fields: Set<string> }>();

      for (const candidateQuery of candidateQueries) {
        try {
          const snapshot = await getDocs(candidateQuery.q);
          if (!snapshot.empty) {
            snapshot.docs.forEach((snap) => {
              const existing = candidates.get(snap.id);
              if (existing) {
                existing.fields.add(candidateQuery.field);
              } else {
                candidates.set(snap.id, {
                  ref: snap.ref,
                  fields: new Set([candidateQuery.field]),
                });
              }
            });
          }
        } catch (error) {
          console.warn(
            `Lead lookup by ${candidateQuery.field} was not available from the client.`,
            error,
          );
        }
      }

      const directMatch = Array.from(candidates.values()).find(
        (candidate) =>
          candidate.fields.has("studentUID") ||
          candidate.fields.has("sessionLeadId"),
      );
      if (directMatch) {
        await updateDoc(directMatch.ref, payload);
        return directMatch.ref;
      }

      const verifiedMatch = Array.from(candidates.values()).find(
        (candidate) =>
          (["ic", "phoneNumber", "email"] as LeadMatchField[]).filter((field) =>
            candidate.fields.has(field),
          ).length >= 2,
      );
      if (verifiedMatch) {
        await updateDoc(verifiedMatch.ref, payload);
        return verifiedMatch.ref;
      }

      const possibleMatches = Array.from(candidates.values())
        .filter((candidate) =>
          (["ic", "phoneNumber", "email"] as LeadMatchField[]).some((field) =>
            candidate.fields.has(field),
          ),
        )
        .map((candidate) => ({
          leadId: candidate.ref.id,
          matchedFields: Array.from(candidate.fields).filter((field) =>
            ["ic", "phoneNumber", "email"].includes(field),
          ),
        }));

      const createdDoc = await addDoc(collection(db, "leads"), {
        ...payload,
        ...(possibleMatches.length ? { possibleMatches } : {}),
        createdAt: serverTimestamp(),
        createdDate: new Date().toISOString().split("T")[0],
        accountStatus: payload.accountStatus || "lead",
        leadSource: payload.leadSource || "Website",
        assignee: payload.assignee || "Unassigned",
        status: payload.status || "New",
      });
      return createdDoc;
    } catch (error) {
      console.error("Lead upsert error:", error);
      return null;
    }
  };

  const fetchStudent = async (uid: string, email: string): Promise<Student> => {
    try {
      const q = query(collection(db, "leads"), where("studentUID", "==", uid));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const snap = querySnapshot.docs[0];
        const data = snap.data();
        return hydrateStudent(data as Partial<Student>, uid, email);
      }
    } catch (error) {
      console.error("Fetch student error:", error);
    }
    return { ...DEFAULT_STUDENT_DATA, id: uid, email };
  };

  const claimStudentLead = async (leadData: Record<string, any>) => {
    const functions = getFunctions(app, "asia-southeast1");
    const claimOrCreateStudentLead = httpsCallable(
      functions,
      "claimOrCreateStudentLead",
    );
    return claimOrCreateStudentLead({ leadData });
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const uid = userCredential.user.uid;
      const functions = getFunctions(app, "asia-southeast1");
      try {
        await finalizePendingLandingPageSubmissions();
      } catch (finalizeError) {
        console.warn(
          "Landing page pending submissions could not finalize.",
          finalizeError,
        );
      }

      for (const finalizer of [
        {
          name: "finalizePendingStudentLead",
          run: () =>
            httpsCallable(functions, "finalizePendingStudentLead")({}),
        },
      ]) {
        try {
          await finalizer.run();
        } catch (finalizeError) {
          const code = String((finalizeError as any)?.code || "");
          if (!code.endsWith("failed-precondition")) {
            console.warn(
              `${finalizer.name} could not finalize pending data.`,
              finalizeError,
            );
          }
        }
      }
      const student = await fetchStudent(uid, email);
      setUser(student);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error("Password reset error:", error);
      return false;
    }
  };

  const register = async (
    data: Partial<Student> & {
      password: string;
      courseFinderAnswers?: CourseFinderAnswers;
      country?: string;
      countryCode?: string;
    },
  ): Promise<boolean> => {
    let createdUser: FirebaseUser | null = null;
    let preserveCreatedUser = false;

    try {
      await setPersistence(auth, browserLocalPersistence);
      // 1. Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email || "",
        data.password,
      );
      const firebaseUser = userCredential.user;
      createdUser = firebaseUser;
      const studentUID = firebaseUser.uid;

      // const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
      const name = data.name || "";
      const phoneNumber = data.phoneNumber || "";
      const email = data.email || "";

      // Destructure out properties we don't want to save directly in the document
      const {
        password: _password,
        courseFinderAnswers: _cfa,
        qualifications: legacyQualifications,
        ...restData
      } = data as any;
      const qualification = data.qualification || legacyQualifications || "";

      const pendingCareerTestResult = getPendingCareerTestResult();
      const sourceDetail = appendSourceDetail(
        undefined,
        ...(data.courseFinderResult ? ["Course Finder"] : []),
        ...(pendingCareerTestResult ? ["Career Test"] : []),
        "Direct Registration",
      );

      // 2. Save to "leads" collection and link it to the student account
      const leadPayload = {
        ...restData,
        qualification,
        name,
        email,
        phoneNumber,
        country: data.country || "Malaysia",
        countryCode: data.countryCode || "60",
        studentUID,
        accountStatus: "registered",
        leadSource: "Website",
        sourceDetail,
        ...(pendingCareerTestResult
          ? { careerTestResult: pendingCareerTestResult }
          : {}),
      };

      try {
        await claimStudentLead(leadPayload);
      } catch (claimError) {
        const code = String((claimError as any)?.code || "");
        if (code.endsWith("failed-precondition") && createdUser) {
          await sendEmailVerification(createdUser);
          preserveCreatedUser = true;
          const verificationError = new Error(
            "Please verify your email before we link your existing student record.",
          );
          (verificationError as any).code = "verification-required";
          throw verificationError;
        }
        throw claimError;
      }

      clearPendingCareerTestResult();

      // 3. Update local state
      setUser({
        ...DEFAULT_STUDENT_DATA,
        ...leadPayload,
        ...restData,
        id: studentUID,
        name: data.name || "New Student",
        email,
        phoneNumber: phoneNumber,
        pathway: (data.pathway as "Local" | "Overseas" | "Both" | "") || "",
        interests: data.interests || [],
        budget: data.budget || "",
        qualification,
        school:
          data.school && typeof data.school === "object"
            ? data.school
            : {
                schoolName: data.school || "",
                schoolState: data.state || "",
                schoolYear: data.spmYear ? String(data.spmYear) : "",
              },
        state: data.state || "",
        profileCompletion: data.profileCompletion,
        courseFinderAnswers: undefined,
      });

      return true;
    } catch (error) {
      console.error("Registration error:", error);

      if (createdUser && !preserveCreatedUser) {
        try {
          await deleteUser(createdUser);
          console.log("Auth user deleted due to registration failure.");
        } catch (deleteError) {
          console.error(
            "Failed to delete partially created Auth user:",
            deleteError,
          );
        }
      }

      if (preserveCreatedUser) {
        throw error;
      }

      return false;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setIsLoading(false);
  };

  const updateUser = async (updates: Partial<Student>) => {
    if (!user) return;
    try {
      // Update leads collection with all data
      const leadsQuery = query(
        collection(db, "leads"),
        where("studentUID", "==", user.id),
      );
      const leadsSnapshot = await getDocs(leadsQuery);
      if (!leadsSnapshot.empty) {
        const leadsRef = leadsSnapshot.docs[0].ref;
        await updateDoc(leadsRef, {
          ...updates,
          ...(updates.qualification !== undefined
            ? { qualifications: deleteField() }
            : {}),
        });
      }

      setUser((prev) =>
        prev
          ? {
              ...prev,
              ...updates,
              ...(updates.qualification !== undefined
                ? { qualifications: undefined }
                : {}),
            }
          : null,
      );
    } catch (error) {
      console.error("Update user error:", error);
    }
  };

  const submitCourseFinderAnswers = async (
    answers: Partial<CourseFinderAnswers>,
  ): Promise<boolean> => {
    if (!user) return true;

    try {
      const courseFinderInterests =
        answers.courseFinderInterests || answers.interests || [];
      const preferredStudyLocations =
        answers.preferredStudyLocations || answers.locations || [];
      const courseFinderUpdates = {
        courseFinderInterests,
        preferredStudyLocations,
        qualification: answers.qualification || answers.qualifications || "",
        estimatedResult: answers.estimatedResult || answers.resultRange || "",
        studentResult: answers.studentResult || "",
        pathway: answers.pathway || "",
        budget: answers.budget || "",
        timeline: answers.timeline || "",
        parentInvolvement: answers.parentInvolvement || "",
        profileCompletion: answers.profileCompletion,
        courseFinderResult: {
          answers: {
            courseFinderInterests,
            qualification:
              answers.qualification || answers.qualifications || "",
            estimatedResult:
              answers.estimatedResult || answers.resultRange || "",
            studentResult: answers.studentResult || "",
            preferredStudyLocations,
            pathway: answers.pathway || "",
            budget: answers.budget || "",
            timeline: answers.timeline || "",
            parentInvolvement: answers.parentInvolvement || "",
          },
          ...((answers as any).recommendations
            ? { recommendations: (answers as any).recommendations }
            : {}),
          completedAt: new Date().toISOString(),
        },
      };
      const firestoreUpdates = {
        ...courseFinderUpdates,
        courseFinderAnswers: deleteField(),
        ...(answers.qualification !== undefined
          ? { qualifications: deleteField() }
          : {}),
      };

      const leadPayload = {
        ...courseFinderUpdates,
        ...(user?.careerTestResult
          ? { careerTestResult: user.careerTestResult }
          : {}),
        ...(user?.name ? { name: user.name } : {}),
        ...(user?.email ? { email: user.email } : {}),
        ...(user?.phoneNumber ? { phoneNumber: user.phoneNumber } : {}),
        ...(user?.id ? { studentUID: user.id } : {}),
        leadSource: "Website",
        sourceDetail: appendSourceDetail(user?.sourceDetail, "Course Finder"),
        accountStatus: user?.id ? "registered" : "lead",
      };

      try {
        await claimStudentLead(leadPayload);
      } catch (claimError) {
        console.error("Secure Course Finder lead update failed.", claimError);
        return false;
      }

      if (user) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                ...courseFinderUpdates,
                courseFinderAnswers: undefined,
                ...(answers.qualification !== undefined
                  ? { qualifications: undefined }
                  : {}),
                ...(answers.estimatedResult !== undefined
                  ? { resultRange: undefined }
                  : {}),
                sourceDetail: appendSourceDetail(
                  prev.sourceDetail,
                  "Course Finder",
                ),
              }
            : null,
        );
      }

      return true;
    } catch (error) {
      console.error("Submit course finder answers error:", error);
      return false;
    }
  };

  const saveCareerTestResult = async (
    result: CareerTestResult,
  ): Promise<boolean> => {
    if (!result) return false;
    if (!user) {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(
          PENDING_CAREER_RESULT_KEY,
          JSON.stringify(result),
        );
      }
      return true;
    }
    try {
      const leadPayload = {
        careerTestResult: result,
        ...(user?.courseFinderResult
          ? { courseFinderResult: user.courseFinderResult }
          : {}),
        ...(user?.courseFinderInterests?.length
          ? { courseFinderInterests: user.courseFinderInterests }
          : {}),
        ...(user?.preferredStudyLocations?.length
          ? { preferredStudyLocations: user.preferredStudyLocations }
          : {}),
        ...(user?.qualification ? { qualification: user.qualification } : {}),
        ...(user?.estimatedResult
          ? { estimatedResult: user.estimatedResult }
          : {}),
        ...(user?.studentResult ? { studentResult: user.studentResult } : {}),
        ...(user?.pathway ? { pathway: user.pathway } : {}),
        ...(user?.budget ? { budget: user.budget } : {}),
        ...(user?.timeline ? { timeline: user.timeline } : {}),
        ...(user?.name ? { name: user.name } : {}),
        ...(user?.email ? { email: user.email } : {}),
        ...(user?.phoneNumber ? { phoneNumber: user.phoneNumber } : {}),
        ...(user?.id ? { studentUID: user.id } : {}),
        leadSource: "Website",
        sourceDetail: appendSourceDetail(user?.sourceDetail, "Career Test"),
        accountStatus: user?.id ? "registered" : "lead",
      };
      try {
        await claimStudentLead(leadPayload);
      } catch (claimError) {
        console.error("Secure Career Test lead update failed.", claimError);
        return false;
      }
      setUser((prev) =>
        prev
          ? {
              ...prev,
              careerTestResult: result,
              sourceDetail: appendSourceDetail(
                prev.sourceDetail,
                "Career Test",
              ),
            }
          : prev,
      );
      return true;
    } catch (error) {
      console.error("Save career test result error:", error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        profileLoadError,
        login,
        resetPassword,
        register,
        logout,
        updateUser,
        submitCourseFinderAnswers,
        saveCareerTestResult,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
